import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query'
import noop from 'lodash/noop'
import { postNamedCommand, postNamedQuery } from '../service'
import { useAppContext } from '../redux/slices/appContext'

export const useNLQuery = (options = {}) => {
  const { onSuccess = noop, enabled = true } = options

  const { userId } = useAppContext()
  return useQuery({
    queryKey: ['denali-query-nlquery', userId],
    queryFn: async () => {
      return []
    },
    onSuccess,
    enabled
  })
}

export const usePostNLQuery = () => {
  const queryClient = useQueryClient()
  const { userId } = useAppContext()
  return useMutation({
    mutationFn: async ({ prompt }) => {
      const { data } = await postNamedQuery('denali', 'nlQuery', {
        prompt
      })

      return data
    },
    onSuccess: (data, command) => {
      queryClient.setQueryData(['denali-query-nlquery', userId], prev => {
        if (!prev) {
          return [{ ...command, result: data?.result }]
        }
        prev.push({ ...command, result: data?.result })
        return prev
      })
    }
  })
}

export const useDenaliAssistantThread = (options = {}) => {
  const { onSuccess = noop, enabled = true } = options

  const { userId } = useAppContext()
  return useQuery({
    queryKey: ['denali-assistant-thread', userId],
    queryFn: async () => {
      return []
    },
    onSuccess,
    enabled
  })
}

export const useInvokeAssistant = () => {
  const queryClient = useQueryClient()
  const { userId } = useAppContext()
  return useMutation({
    mutationFn: async ({ prompt }) => {
      const { data } = await postNamedCommand('denali', 'invoke-assistant', {
        prompt
      })

      return data
    },
    onSuccess: (data, command) => {
      queryClient.setQueryData(['denali-assistant-thread', userId], prev => {
        if (!prev) {
          return [{ queryId: data.queryId }]
        }
        prev.push({ queryId: data.queryId })
        return prev
      })
    }
  })
}

export const useAssistantQuery = (queryId, options = {}) => {
  const { userId } = useAppContext()
  const { enabled = true, refetchInterval = null } = options
  return useQuery({
    queryKey: ['denali-assistant-query', userId, queryId],
    queryFn: async () => {
      const { data } = await postNamedQuery('denali', 'get-assistant-query', {
        queryId
      })

      return data
    },
    enabled,
    refetchInterval,
    refetchOnWindowFocus: !!refetchInterval
  })
}

// Query Blueprints - Queries
export const useSearchQueryBlueprints = (query = {}, options = {}) => {
  const { enabled = true } = options
  return useQuery({
    queryKey: ['search-query-blueprints', query],
    queryFn: async () => {
      const { data } = await postNamedQuery('coreData', 'search-query-blueprints', query)
      return data
    },
    enabled
  })
}

export const useGetQueryBlueprint = (blueprintId, options = {}) => {
  const { enabled = !!blueprintId, includes = {} } = options
  return useQuery({
    queryKey: ['get-query-blueprint', blueprintId, includes],
    queryFn: async () => {
      const { data } = await postNamedQuery('coreData', 'get-query-blueprint', {
        blueprintId,
        includes
      })
      return data
    },
    enabled
  })
}

export const useGetQueryBlueprintByCode = (code, options = {}) => {
  const { enabled = !!code, includes = {} } = options
  return useQuery({
    queryKey: ['get-query-blueprint', 'code', code, includes],
    queryFn: async () => {
      const { data } = await postNamedQuery('coreData', 'get-query-blueprint', {
        code,
        includes
      })
      return data
    },
    enabled
  })
}

export const useGetQueryBlueprintDefinition = (definitionId, options = {}) => {
  const { enabled = !!definitionId } = options
  return useQuery({
    queryKey: ['get-query-blueprint-definition', definitionId],
    queryFn: async () => {
      const { data } = await postNamedQuery('coreData', 'get-query-blueprint-definition', {
        definitionId
      })
      return data
    },
    enabled
  })
}

export const useListQueryBlueprintVersions = (blueprintId, options = {}) => {
  const { enabled = !!blueprintId } = options
  return useQuery({
    queryKey: ['list-query-blueprint-versions', blueprintId],
    queryFn: async () => {
      const { data } = await postNamedQuery('coreData', 'list-query-blueprint-versions', {
        blueprintId
      })
      return data
    },
    enabled
  })
}

// Query Blueprints - Mutations
export const useCreateQueryBlueprint = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (command) => {
      const { data } = await postNamedCommand('coreData', 'create-query-blueprint', command)
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['search-query-blueprints'])
    }
  })
}

export const useModifyQueryBlueprint = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (command) => {
      const { data } = await postNamedCommand('coreData', 'modify-query-blueprint', command)
      return data
    },
    onSuccess: (data, command) => {
      queryClient.invalidateQueries(['search-query-blueprints'])
      queryClient.invalidateQueries(['get-query-blueprint', command.blueprintId])
    }
  })
}

export const useRemoveQueryBlueprint = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (command) => {
      const { data } = await postNamedCommand('coreData', 'remove-query-blueprint', command)
      return data
    },
    onSuccess: (data, command) => {
      queryClient.invalidateQueries(['search-query-blueprints'])
      queryClient.removeQueries(['get-query-blueprint', command.blueprintId])
      queryClient.removeQueries(['list-query-blueprint-versions', command.blueprintId])
    }
  })
}

export const useAddQueryBlueprintDefinition = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (command) => {
      const { data } = await postNamedCommand('coreData', 'add-query-blueprint-definition', command)
      return data
    },
    onSuccess: (data, command) => {
      queryClient.invalidateQueries(['get-query-blueprint', command.blueprintId])
      queryClient.invalidateQueries(['list-query-blueprint-versions', command.blueprintId])
    }
  })
}

export const useModifyQueryBlueprintDefinition = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (command) => {
      const { data } = await postNamedCommand('coreData', 'modify-query-blueprint-definition', command)
      return data
    },
    onSuccess: (data, command) => {
      queryClient.invalidateQueries(['get-query-blueprint', command.blueprintId])
      queryClient.invalidateQueries(['list-query-blueprint-versions', command.blueprintId])
    }
  })
}

export const useRemoveQueryBlueprintVersion = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (command) => {
      const { data } = await postNamedCommand('coreData', 'remove-query-blueprint-version', command)
      return data
    },
    onSuccess: (data, command) => {
      queryClient.invalidateQueries(['list-query-blueprint-versions', command.blueprintId])
      queryClient.invalidateQueries(['get-query-blueprint', command.blueprintId])
    }
  })
}

export const useSetActiveQueryBlueprintVersion = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (command) => {
      const { data } = await postNamedCommand('coreData', 'set-active-query-blueprint-version', command)
      return data
    },
    onSuccess: (data, command) => {
      queryClient.invalidateQueries(['get-query-blueprint', command.blueprintId])
      queryClient.invalidateQueries(['search-query-blueprints'])
    }
  })
}

export const useParseBlueprintFromDql = () => {
  return useMutation({
    mutationFn: async (command) => {
      const { data } = await postNamedCommand('coreData', 'parse-blueprint-from-dql', command)
      return data
    }
  })
}

export const useAnalyzeDqlQuery = () => {
  return useMutation({
    mutationFn: async (request) => {
      const { data } = await postNamedQuery('coreData', 'analyze-dql-query', request)
      return data
    }
  })
}

export const useExecuteQueryBlueprint = () => {
  return useMutation({
    mutationFn: async (request) => {
      const { data } = await postNamedQuery('coreData', 'execute-query-blueprint', request)
      return data
    }
  })
}

export const useExecuteQueryBlueprintQuery = (request, options = {}) => {
  const enabled = options?.enabled ?? true
  const requestKey = JSON.stringify(request)
  const code = request?.code ?? 'unknown'
  const purposeCode = request?.purposeCode ?? 'default'
  return useQuery({
    queryKey: ['execute-query-blueprint', code, purposeCode, requestKey],
    queryFn: async () => {
      const { data } = await postNamedQuery('coreData', 'execute-query-blueprint', request)
      return data
    },
    enabled,
    keepPreviousData: true
  })
}

export const useGetBlueprintLatchDates = (code, purposeCode, options = {}) => {
  const { enabled = !!code && !!purposeCode, limit = 100 } = options
  return useQuery({
    queryKey: ['get-blueprint-latch-dates', code, purposeCode, limit],
    queryFn: async () => {
      const { data } = await postNamedQuery('coreData', 'get-blueprint-latch-dates', {
        code,
        purposeCode,
        limit
      })
      return data
    },
    enabled
  })
}

export const useGetBlueprintFacetValues = (code, purposeCode, column, options = {}) => {
  const { enabled = !!code && !!purposeCode && !!column, limit = 200, parameters } = options
  return useQuery({
    queryKey: ['get-blueprint-facet-values', code, purposeCode, column, limit, parameters],
    queryFn: async () => {
      const req = { code, purposeCode, column, limit }
      if (parameters) req.parameters = parameters
      const { data } = await postNamedQuery('coreData', 'get-blueprint-facet-values', req)
      return data
    },
    enabled
  })
}

// Component Blueprints - Queries
export const useSearchComponentBlueprints = (query = {}, options = {}) => {
  const { enabled = true } = options
  return useQuery({
    queryKey: ['search-component-blueprints', query],
    queryFn: async () => {
      const { data } = await postNamedQuery('coreData', 'search-component-blueprints', query)
      return data
    },
    enabled
  })
}

export const useGetComponentBlueprint = (blueprintId, options = {}) => {
  const { enabled = !!blueprintId, includes = {} } = options
  return useQuery({
    queryKey: ['get-component-blueprint', blueprintId, includes],
    queryFn: async () => {
      const { data } = await postNamedQuery('coreData', 'get-component-blueprint', {
        blueprintId,
        includes
      })
      return data
    },
    enabled
  })
}

export const useGetComponentBlueprintByCode = (code, options = {}) => {
  const { enabled = !!code, includes = {} } = options
  return useQuery({
    queryKey: ['get-component-blueprint', 'code', code, includes],
    queryFn: async () => {
      const { data } = await postNamedQuery('coreData', 'get-component-blueprint', {
        code,
        includes
      })
      return data
    },
    enabled
  })
}

export const usePreloadComponentBlueprints = (blueprintCode) => {
  const codes = Array.isArray(blueprintCode) ? blueprintCode : [blueprintCode]
  const includes = { definition: true }

  const results = useQueries({
    queries: codes.filter(Boolean).map(code => ({
      queryKey: ['get-component-blueprint', 'code', code, includes],
      queryFn: async () => {
        const { data } = await postNamedQuery('coreData', 'get-component-blueprint', {
          code,
          includes
        })
        return data
      },
      enabled: !!code
    }))
  })

  return {
    isLoading: results.some(r => r.isLoading)
  }
}

export const useGetComponentBlueprintDefinition = (definitionId, options = {}) => {
  const { enabled = !!definitionId } = options
  return useQuery({
    queryKey: ['get-component-blueprint-definition', definitionId],
    queryFn: async () => {
      const { data } = await postNamedQuery('coreData', 'get-component-blueprint-definition', {
        definitionId
      })
      return data
    },
    enabled
  })
}

export const useListComponentBlueprintVersions = (blueprintId, options = {}) => {
  const { enabled = !!blueprintId } = options
  return useQuery({
    queryKey: ['list-component-blueprint-versions', blueprintId],
    queryFn: async () => {
      const { data } = await postNamedQuery('coreData', 'list-component-blueprint-versions', {
        blueprintId
      })
      return data
    },
    enabled
  })
}

// Component Blueprints - Mutations
export const useCreateComponentBlueprint = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (command) => {
      const { data } = await postNamedCommand('coreData', 'create-component-blueprint', command)
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['search-component-blueprints'])
    }
  })
}

export const useModifyComponentBlueprint = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (command) => {
      const { data } = await postNamedCommand('coreData', 'modify-component-blueprint', command)
      return data
    },
    onSuccess: (data, command) => {
      queryClient.invalidateQueries(['search-component-blueprints'])
      queryClient.invalidateQueries(['get-component-blueprint', command.blueprintId])
    }
  })
}

export const useRemoveComponentBlueprint = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (command) => {
      const { data } = await postNamedCommand('coreData', 'remove-component-blueprint', command)
      return data
    },
    onSuccess: (data, command) => {
      queryClient.invalidateQueries(['search-component-blueprints'])
      queryClient.removeQueries(['get-component-blueprint', command.blueprintId])
      queryClient.removeQueries(['list-component-blueprint-versions', command.blueprintId])
    }
  })
}

export const useAddComponentBlueprintDefinition = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (command) => {
      const { data } = await postNamedCommand('coreData', 'add-component-blueprint-definition', command)
      return data
    },
    onSuccess: (data, command) => {
      queryClient.invalidateQueries(['get-component-blueprint', command.blueprintId])
      queryClient.invalidateQueries(['list-component-blueprint-versions', command.blueprintId])
    }
  })
}

export const useModifyComponentBlueprintDefinition = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (command) => {
      const { data } = await postNamedCommand('coreData', 'modify-component-blueprint-definition', command)
      return data
    },
    onSuccess: (data, command) => {
      queryClient.invalidateQueries(['get-component-blueprint', command.blueprintId])
      queryClient.invalidateQueries(['list-component-blueprint-versions', command.blueprintId])
    }
  })
}

export const useRemoveComponentBlueprintVersion = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (command) => {
      const { data } = await postNamedCommand('coreData', 'remove-component-blueprint-version', command)
      return data
    },
    onSuccess: (data, command) => {
      queryClient.invalidateQueries(['list-component-blueprint-versions', command.blueprintId])
      queryClient.invalidateQueries(['get-component-blueprint', command.blueprintId])
    }
  })
}

export const useSetActiveComponentBlueprintVersion = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (command) => {
      const { data } = await postNamedCommand('coreData', 'set-active-component-blueprint-version', command)
      return data
    },
    onSuccess: (data, command) => {
      queryClient.invalidateQueries(['get-component-blueprint', command.blueprintId])
      queryClient.invalidateQueries(['search-component-blueprints'])
    }
  })
}

// User DQL Queries - Queries
export const useListUserDqlQueries = (options = {}) => {
  const { enabled = true, favoritesOnly = false } = options
  const { userId } = useAppContext()

  return useQuery({
    queryKey: ['list-user-dql-queries', userId, favoritesOnly],
    queryFn: async () => {
      const { data } = await postNamedQuery('coreData', 'list-user-dql-queries', {
        favoritesOnly
      })
      return data
    },
    enabled
  })
}

export const useGetUserDqlQuery = (userDqlQueryId, options = {}) => {
  const { enabled = !!userDqlQueryId } = options

  return useQuery({
    queryKey: ['get-user-dql-query', userDqlQueryId],
    queryFn: async () => {
      const { data } = await postNamedQuery('coreData', 'get-user-dql-query', {
        userDqlQueryId
      })
      return data
    },
    enabled
  })
}

// User DQL Queries - Mutations
export const useCreateUserDqlQuery = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (command) => {
      const { data } = await postNamedCommand('coreData', 'create-user-dql-query', command)
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['list-user-dql-queries'] }).catch(console.error)
    }
  })
}

export const useModifyUserDqlQuery = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (command) => {
      const { data } = await postNamedCommand('coreData', 'modify-user-dql-query', command)
      return data
    },
    onSuccess: (data, command) => {
      queryClient.invalidateQueries({ queryKey: ['list-user-dql-queries'] }).catch(console.error)
      queryClient.invalidateQueries({ queryKey: ['get-user-dql-query', command.userDqlQueryId] }).catch(console.error)
    }
  })
}

export const useRemoveUserDqlQuery = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (command) => {
      const { data } = await postNamedCommand('coreData', 'remove-user-dql-query', command)
      return data
    },
    onSuccess: (data, command) => {
      queryClient.invalidateQueries({ queryKey: ['list-user-dql-queries'] }).catch(console.error)
      queryClient.removeQueries({ queryKey: ['get-user-dql-query', command.userDqlQueryId] })
    }
  })
}

export const useFavoriteUserDqlQuery = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (command) => {
      const { data } = await postNamedCommand('coreData', 'favorite-user-dql-query', command)
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['list-user-dql-queries'] }).catch(console.error)
    }
  })
}
